import { useCallback, useEffect, useRef, useState } from 'react'

export const getRandomUploadTickSeconds = (randomValue = Math.random()) =>
  Math.floor(randomValue * 6) + 1

export const getDelayForProgress = (progress, randomValue = Math.random()) => {
  if (progress < 70) {
    return getRandomUploadTickSeconds(randomValue) * 1000
  }

  if (progress < 90) {
    return 60 * 1000
  }

  if (progress < 99) {
    return 300 * 1000
  }

  return null
}

export const buildSuccessRampSteps = startValue => {
  let current = Math.max(0, Math.min(99, startValue || 0))
  const steps = []

  while (current < 100) {
    const remaining = 100 - current
    const delta = remaining > 28 ? 4 : remaining > 16 ? 3 : remaining > 8 ? 2 : 1
    current = Math.min(100, current + delta)
    steps.push(current)
  }

  return steps
}

const getAttachmentKey = attachmentId => String(attachmentId)

const clearAttachmentTimer = (timersRef, attachmentKey) => {
  if (timersRef.current[attachmentKey]) {
    clearTimeout(timersRef.current[attachmentKey])
    delete timersRef.current[attachmentKey]
  }
}

export const useDummyAttachmentProgress = attachments => {
  const [progressById, setProgressById] = useState({})
  const progressByIdRef = useRef({})
  const timersRef = useRef({})

  const scheduleNextTick = useCallback(attachmentId => {
    const attachmentKey = getAttachmentKey(attachmentId)
    clearAttachmentTimer(timersRef, attachmentKey)

    const currentProgress = progressByIdRef.current[attachmentKey] || 0
    const delay = getDelayForProgress(currentProgress)

    if (!delay) {
      return
    }

    timersRef.current[attachmentKey] = setTimeout(() => {
      const latestProgress = progressByIdRef.current[attachmentKey] || 0
      const nextProgress = Math.min(99, latestProgress + 1)

      if (nextProgress !== latestProgress) {
        const updated = {
          ...progressByIdRef.current,
          [attachmentKey]: nextProgress,
        }

        progressByIdRef.current = updated
        setProgressById(updated)
      }

      scheduleNextTick(attachmentKey)
    }, delay)
  }, [])

  useEffect(() => {
    const validAttachmentIds = new Set(
      attachments
        .filter(
          attachment =>
            attachment.upload_status !== 'partial_failure' &&
            attachment.upload_status !== 'failed' &&
            !(attachment.error_details && attachment.error_details.length > 0),
        )
        .map(attachment => getAttachmentKey(attachment.attachment_id)),
    )

    const processingIds = new Set(
      attachments
        .filter(
          attachment =>
            attachment.succeeded === false &&
            attachment.upload_status !== 'partial_failure' &&
            attachment.upload_status !== 'failed' &&
            !(attachment.error_details && attachment.error_details.length > 0),
        )
        .map(attachment => getAttachmentKey(attachment.attachment_id)),
    )

    processingIds.forEach(attachmentId => {
      if (progressByIdRef.current[attachmentId] == null) {
        const updated = {
          ...progressByIdRef.current,
          [attachmentId]: 0,
        }

        progressByIdRef.current = updated
        setProgressById(updated)
      }

      if (!timersRef.current[attachmentId]) {
        scheduleNextTick(attachmentId)
      }
    })

    Object.keys(timersRef.current).forEach(attachmentId => {
      if (!processingIds.has(attachmentId)) {
        clearAttachmentTimer(timersRef, attachmentId)
      }
    })

    const staleIds = Object.keys(progressByIdRef.current).filter(
      attachmentId => !validAttachmentIds.has(attachmentId),
    )

    if (staleIds.length > 0) {
      const updated = { ...progressByIdRef.current }
      staleIds.forEach(attachmentId => {
        clearAttachmentTimer(timersRef, attachmentId)
        delete updated[attachmentId]
      })
      progressByIdRef.current = updated
      setProgressById(updated)
    }
  }, [attachments, scheduleNextTick])

  useEffect(() => {
    return () => {
      Object.values(timersRef.current).forEach(timerId => clearTimeout(timerId))
      timersRef.current = {}
    }
  }, [])

  return progressById
}
